import React, { useState, useEffect, useMemo } from "react";
import { Check, ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useFestivalRegistry } from "@/hooks/use-festival-registry";
import type { FestivalRegistryEntry } from "@/festival-data";
import { useAuth } from "@/hooks/use-auth";
import { getArchetypeById } from "@/lib/traveler-archetypes";
import { COUNTRY_NAMES } from "@/lib/geo-constants";
import { formatFestivalDates } from "@/lib/date-utils";

interface ValueRevealStepProps {
  onDone: () => void;
}

/** Extended festival entry with optional API-provided fields not in the base type */
type ExtendedFestival = FestivalRegistryEntry & {
  genres?: string[];
  tags?: string[];
  type?: string;
  heatRating?: number;
  city?: string;
};

/** Map archetype interests to festival categories for basic matching */
function pickFestivalsForArchetype(festivals: FestivalRegistryEntry[], archetypeId: string | undefined, count: number) {
  if (!festivals || festivals.length === 0) return [];

  const archetype = archetypeId ? getArchetypeById(archetypeId) : null;
  const interests = archetype?.profile?.interests?.categories || [];

  // Score festivals by interest overlap (simple heuristic)
  const scored = festivals.map((f) => {
    const ext = f as ExtendedFestival;
    const tags = [
      ...(ext.genres || []),
      ...(ext.tags || []),
      ext.type || "",
      f.category || "",
    ].map((t: string) => t.toLowerCase());

    let score = 0;
    for (const interest of interests) {
      const lower = interest.toLowerCase().replace(/_/g, " ");
      if (tags.some((tag) => tag.includes(lower) || lower.includes(tag))) {
        score += 1;
      }
    }
    // Boost popular festivals
    const heat = ext.heatRating;
    if (heat && heat >= 80) score += 0.5;
    return { festival: f, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.festival);
}

const CHECKMARKS = [
  { key: "itineraries", icon: Check },
  { key: "matches", icon: Check },
  { key: "festivals", icon: Check },
] as const;

export const ValueRevealStep = React.memo(function ValueRevealStep({
  onDone,
}: ValueRevealStepProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: allFestivals = [] } = useFestivalRegistry();
  const [visibleChecks, setVisibleChecks] = useState(0);
  const [showFestivals, setShowFestivals] = useState(false);

  const archetypeId = (user as { travelerProfile?: { archetype?: string } } | null)?.travelerProfile?.archetype;
  const suggestedFestivals = useMemo(
    () => pickFestivalsForArchetype(allFestivals, archetypeId, 3),
    [allFestivals, archetypeId],
  );

  // Stagger checkmark animation
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= 3; i++) {
      timers.push(setTimeout(() => setVisibleChecks(i), i * 300));
    }
    timers.push(setTimeout(() => setShowFestivals(true), 1200));
    return () => timers.forEach(clearTimeout);
  }, []);

  const checkLabels = [
    t("travelDna.unlockItineraries"),
    t("travelDna.unlockMatches"),
    t("travelDna.unlockFestivals"),
  ];

  return (
    <div className="flex flex-col items-center text-center gap-5 py-4">
      <h2 className="font-serif text-xl sm:text-2xl font-bold">
        {t("travelDna.valueRevealTitle")}
      </h2>

      {/* Animated checkmarks */}
      <div className="flex flex-col gap-3 w-full max-w-xs">
        {checkLabels.map((label, idx) => (
          <div
            key={idx}
            className={`flex items-center gap-3 transition-all duration-300 ${
              idx < visibleChecks
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-4"
            }`}
          >
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500/10 shrink-0">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <span className="text-sm font-medium text-left">{label}</span>
          </div>
        ))}
      </div>

      {/* Festival suggestions */}
      {showFestivals && suggestedFestivals.length > 0 && (
        <div
          className="w-full space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-500"
        >
          <p className="text-sm font-medium text-muted-foreground">
            {t("travelDna.festivalsMatchVibe", { count: suggestedFestivals.length })}
          </p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {suggestedFestivals.map((f) => (
              <div
                key={f.id}
                className="shrink-0 w-36 sm:w-40 rounded-lg border bg-card p-3 text-left"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="text-xs font-semibold truncate">
                    {f.name}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {(f as ExtendedFestival).city || f.location}, {COUNTRY_NAMES[f.country] || f.country}
                </p>
                {f.dates && f.dates.length > 0 && (
                  <p className="text-xs text-muted-foreground/70 mt-0.5">
                    {formatFestivalDates(f.dates)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA button */}
      <Button
        onClick={onDone}
        className="w-full max-w-xs gap-2 mt-2"
        size="lg"
        data-testid="button-start-exploring"
      >
        {t("travelDna.startExploring")}
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
        {t(
          "travelDna.fitPrefsNudge",
          "Add travel preferences anytime in Settings — they power Trip fit scores on your itineraries.",
        )}{" "}
        <a
          href="/travel-dna"
          className="text-brand-gold underline-offset-2 hover:underline"
          onClick={(e) => {
            e.preventDefault();
            onDone();
            window.location.assign("/travel-dna");
          }}
        >
          {t("travelDna.takeQuiz", "Take the Quiz (60 seconds)")}
        </a>
      </p>
    </div>
  );
});
